import { useState } from "react";
import apiService from "services/api";
import { useHistory, Link } from "react-router-dom";
import { useQuery } from "react-query";
import { useNotificationContext } from "contexts";
import { FormGroup, Label, Input } from "reactstrap";
import { useForm } from "react-hook-form";
import { FormEntry, FormSelect } from "components";
import _ from "lodash";

const initialState = {
	username: "",
	email: "",
	roleId: "",
	password: "",
	fullName: "",
	birthday: "",
	officialStartDate: "",
	personnelId: "",
	status: "Post Probation",
	employeeType: "PT",
	annualLeaveDaysCount: 0,
	compensationLeaveHoursCount: 0,
	sickLeaveDaysCount: 0,
	unpaidLeaveDaysCount: 0,
	residingIsland: "", //New
	hourlyRate: null,
	rateEffectiveDate: null,
	ictTestCompletionDate: null,
	legacyTotalTeachingHours: 0,
	rfidNumber: "",
};

const statusTypes = [
	{ value: "Post Probation", label: "Post Probation" },
	{ value: "Probation", label: "Probation" },
];
const employeeTypes = [
	{ value: "PT", label: "Part Time" },
	{ value: "FT", label: "Full Time" },
];
const regions = [
	{ value: "None", label: "None" },
	{ value: "HK-Island", label: "HK-Island" },
	{ value: "New Territories / Kowloon", label: "New Territories / Kowloon" },
];

const NewStaffPage = () => {
	const history = useHistory();

	const {
		register,
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { errors },
	} = useForm({
		defaultValues: initialState,
	});

	const { data: roles, isLoading: isRolesLoading } = useQuery("role", () => apiService.role.get(), {
		onSuccess: (data) => setValue("roleId", data[0].id),
	});
	const { createNotification } = useNotificationContext();
	const [showPassword, setShowPassword] = useState(false);

	const submitNewStaffForm = (data) => {
		const sanitizedData = _.pickBy(data, (value) => !_.isNull(value) && value !== "");
		apiService.staff
			.create(sanitizedData)
			.then(() => {
				createNotification({
					type: "success",
					message: "Successfully created a new staff",
				});
				history.push("/staff");
			})
			.catch((error) =>
				createNotification({
					type: "danger",
					message: error.message,
				})
			);
	};

	const resetFields = () => reset(initialState);

	return (
		<form onSubmit={handleSubmit(submitNewStaffForm)} className="content">
			<div className="d-flex justify-content-center card">
				<div className="card-body p-sm-5">
					<div className="mb-4">
						<Link to="/staff">
							<button className="btn btn-sm btn-info" type="button">
								&lt; Back to staff
							</button>
						</Link>
					</div>
					<div className="text-center mb-4 font-weight-bold display-5 text-white">Add New Staff</div>
					<FormEntry
						type="text"
						placeholder="Username"
						formLabel="Username"
						name="username"
						register={register}
						error={errors.username}
						required
					/>
					<FormEntry
						type="text"
						placeholder="Personnel's ID"
						formLabel="Personnel ID"
						name="personnelId"
						register={register}
						error={errors.personnelId}
						required
					/>
					<FormEntry
						type="email"
						placeholder="Staff's email"
						formLabel="Email"
						name="email"
						error={errors.email}
						register={register}
						required
					/>

					<div className="d-flex">
						<FormEntry
							type={!showPassword ? "password" : "text"}
							required
							name="password"
							formLabel="Password"
							register={register}
							error={errors.password}
							placeholder="Staff Account's Password"
						/>
						<FormGroup check className="ml-3">
							<Label className="form-check-label">
								<Input
									className="form-check-input"
									type="checkbox"
									onChange={() => setShowPassword((prevState) => !prevState)}
									value={showPassword}
								/>
								Show
								<span className="form-check-sign">
									<span className="check"></span>
								</span>
							</Label>
						</FormGroup>
					</div>

					<FormEntry
						type="text"
						placeholder="Staff's Full Name"
						formLabel="Full Name"
						name="fullName"
						register={register}
						error={errors.fullName}
						required
					/>

					<FormEntry type="date" formLabel="Birthday" name="birthday" register={register} error={errors.birthday} required />

					<FormEntry
						type="date"
						formLabel="Official Start Date"
						name="officialStartDate"
						register={register}
						error={errors.officialStartDate}
						required
					/>

					<div className="input-group mb-3">
						<div className="input-group-prepend">
							<label className="input-group-text pr-2" htmlFor="Role">
								Role
							</label>
						</div>
						<select
							className="form-control custom-select"
							onChange={(e) => setValue("roleId", e.target.value)}
							disabled={isRolesLoading}
							required
						>
							{!isRolesLoading &&
								roles.map((role, key) => (
									<option key={key} value={role.id}>
										{role.name}
									</option>
								))}
						</select>
						{Boolean(errors.roleId) && <span className="my-1">{errors.roleId?.message}</span>}
					</div>

					<FormSelect formLabel="Status" register={register} options={statusTypes} error={errors.status} name="status" required />

					<FormSelect
						formLabel="Employee Type"
						register={register}
						options={employeeTypes}
						error={errors.employeeType}
						name="employeeType"
						required
					/>

					{watch("employeeType") === "PT" && (
						<div>
							<FormEntry type="number" formLabel="Hourly Rate" register={register} error={errors.hourlyRate} name="hourlyRate" />

							<FormEntry
								type="date"
								formLabel="Rate Effective Date"
								register={register}
								error={errors.rateEffectiveDate}
								name="rateEffectiveDate"
							/>

							<FormSelect
								formLabel="Residing Island"
								register={register}
								options={regions}
								error={errors.status}
								name="residingIsland"
							/>
						</div>
					)}

					<FormEntry
						type="date"
						formLabel="ICT Test Completion Date"
						register={register}
						error={errors.ictTestCompletionDate}
						name="ictTestCompletionDate"
					/>
					<FormEntry
						type="number"
						formLabel="Old Total Teaching Hours"
						register={register}
						error={errors.legacyTotalTeachingHours}
						step="0.25"
						name="legacyTotalTeachingHours"
					/>
					<FormSelect formLabel="Region" register={register} options={regions} error={errors.region} name="region" required />

					<FormEntry
						type="number"
						formLabel="Annual Leaves"
						name="annualLeaveDaysCount"
						register={register}
						error={errors.annualLeaveDaysCount}
						required
					/>

					<FormEntry
						type="number"
						formLabel="Compensation Leaves"
						name="compensationLeaveHoursCount"
						register={register}
						error={errors.compensationLeaveHoursCount}
						required
					/>

					<FormEntry
						type="number"
						formLabel="Sick Leaves"
						name="sickLeaveDaysCount"
						register={register}
						error={errors.sickLeaveDaysCount}
						required
					/>
					<FormEntry
						type="number"
						formLabel="Unpaid Leaves"
						name="unpaidLeaveDaysCount"
						register={register}
						error={errors.unpaidLeaveDaysCount}
						required
					/>
					<FormEntry type="text" formLabel="RFID Number" name="rfidNumber" register={register} error={errors.rfidNumber} />

					{/* annualLeaveDaysCount: "", compensationLeaveHoursCount: "",
                    sickLeaveDaysCount: "", unpaidLeaveDaysCount: "", */}
					<div className="d-flex justify-content-end mt-5">
						<button className="btn btn-success mx-1" type="submit">
							Submit
						</button>
						<button className="btn btn-secondary" type="button" onClick={resetFields}>
							Reset
						</button>
					</div>
				</div>
			</div>
		</form>
	);
};

export default NewStaffPage;
